import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';

const CertificateDownload = ({ courseId, studentData }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const downloadCertificate = async () => {
    try {
      setLoading(true);

      // Call backend API to generate certificate
      const response = await axios.get(`/api/certificates/${courseId}/certificate-download`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPreviewUrl(url);

      const link = document.createElement('a');
      link.href = url;
      link.download = `Certificate-${courseId}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Set timeout to revoke URL to free memory
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);

    } catch (err) {
      console.error('Certificate download error:', err);
      alert('Failed to download certificate. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const previewCertificate = async () => {
    try {
      setLoading(true);

      // Get preview URL
      const response = await axios.get(`/api/certificates/${courseId}/certificate-preview`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPreviewUrl(url);

    } catch (err) {
      console.error('Certificate preview error:', err);
      alert('Failed to load certificate preview.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="certificate-container bg-white p-6 rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-center mb-4">Course Completion Certificate</h2>

        {/* Certificate Preview */}
        {previewUrl && (
          <div className="certificate-preview bg-gray-100 p-4 rounded-lg border-2 border-dashed border-gray-300 mb-4">
            <img
              src={previewUrl}
              alt="Certificate Preview"
              className="max-w-full h-auto mx-auto block"
              style={{
                maxWidth: '100%',
                maxHeight: '600px',
              }}
            />
          </div>
        )}

        {!previewUrl && (
          <div className="certificate-preview bg-gray-100 p-4 rounded-lg border-2 border-dashed border-gray-300 mb-4">
            <div className="text-center py-12 text-gray-500">
              <DocumentArrowDownIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p>Click "Preview Certificate" to see your certificate before downloading.</p>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={previewCertificate}
          disabled={loading}
          className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          {loading ? 'Loading...' : 'Preview Certificate'}
        </button>

        <button
          onClick={downloadCertificate}
          disabled={loading}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <DocumentArrowDownIcon className="w-5 h-5" />
          {loading ? 'Downloading...' : 'Download Certificate'}
        </button>
      </div>

      {/* Instructions */}
      <div className="mt-6 bg-blue-50 p-4 rounded-lg">
        <h3 className="font-semibold mb-2 text-blue-900">How to Get Your Certificate:</h3>
        <ol className="list-decimal list-inside text-blue-800 text-sm space-y-1">
          <li>Preview your certificate above</li>
          <li>If it looks good, click "Download Certificate" to save it</li>
          <li>The certificate will be downloaded as a high-quality JPG image</li>
          <li>Certificates are prepared by your instructor after grading</li>
        </ol>
      </div>
    </div>
  );
};

export default CertificateDownload;
